// ---------------------------------------------------------------------------
// MatrixCardEmptyScope (US5 / PRODOPSAI-398)
//
// Sibling of `MatrixCard` used when an `IMatrixCardSpec` has zero rows AND
// no org-row aggregate — i.e. the active Scope filter yields no data. The
// card still renders the full chrome (title + subtitle + legend) so the
// page's three-card rhythm is preserved; only the body is replaced with a
// centred empty-scope placeholder.
//
// Pure presentation: no fetching, no async work. Like `MatrixCard`, every
// visible string except the placeholder copy flows through `IMatrixCardSpec`.
// The placeholder copy itself is generic ("No teams in the active scope. Try
// changing Scope above.") and intentionally per-tab — see US5 spec ACs.
// ---------------------------------------------------------------------------

import type { ILegendChip, IMatrixCardSpec } from '@skystone/shared';
import { Card, CardContent, CardHeader } from '@/components/ui';
import { cn } from '@/lib/utils';

const TONE_SWATCH_CLASS: Record<ILegendChip['tone'], string> = {
  success: 'bg-success/15 border-success/40',
  warning: 'bg-warning/15 border-warning/40',
  error: 'bg-error/15 border-error/40',
};

interface ChipProps {
  chip: ILegendChip;
}

function Chip({ chip }: Readonly<ChipProps>) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <span
        aria-hidden="true"
        className={cn('inline-block h-2.5 w-3 rounded-sm border', TONE_SWATCH_CLASS[chip.tone])}
      />
      {chip.label}
    </span>
  );
}

interface MatrixCardEmptyScopeProps {
  spec: IMatrixCardSpec;
}

export function MatrixCardEmptyScope({ spec }: Readonly<MatrixCardEmptyScopeProps>) {
  return (
    <Card
      data-testid="matrix-card-empty-scope"
      data-metric-key={spec.metricKey}
    >
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0 flex-1 space-y-0.5">
            <h3 className="break-words text-sm font-bold leading-tight text-foreground">{spec.title}</h3>
            <p className="text-[11px] text-muted-foreground">{spec.subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {spec.legend.map((chip) => (
              <Chip key={`${chip.tone}-${chip.label}`} chip={chip} />
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="py-8 text-center text-sm text-muted-foreground">
          No teams in the active scope. Try changing Scope above.
        </p>
      </CardContent>
    </Card>
  );
}

export default MatrixCardEmptyScope;
